import { css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import { IPlace, IPlaceReport } from "../../models/Models";
import { ifDefined } from "lit/directives/if-defined.js";
import { watch } from "../internal/watch";
import { LightTheme } from "../ThemeLight";
import { litIf } from "../LitUtils";
import { ReportSwell, ReportVisibility, ReportWind } from "../../../models";
import { formatDateTime } from "../../Format";
import { userCanEditPlaceReport } from "../../backend/IPlaceReportTable";

import "../Temperature/Temperature";
import { IPlaceTable } from "../../backend/IPlaceTable";

@customElement("ds-place-report")
export class PlaceReport extends BaseElement {
    static override styles = [
        Style,
        LightTheme,
        css`
            :host {
                display: block;
            }

            .container {
                width: 100%;
            }

            .header {
                justify-content: space-between;
            }

            .name {
                font-weight: bold;
            }

            .properties {
                display: grid;
                grid-template-columns: 1fr 1fr;
            }

            .trip-report-place-name {
                margin: 0;
                padding: 0;
            }

            sl-rating {
                --symbol-size: 0.8em;
            }

            sl-card {
                width: 100%;
            }
        `,
    ];

    @property({ type: Object }) report?: IPlaceReport;
    @property({ type: Boolean }) showPlaceDetails = false;
    @state() protected _ownerName?: string;
    @state() protected _date?: string;
    @state() protected _userAvatarUrl?: string;
    @state() protected _canEdit = false;
    @state() protected _place?: IPlace;

    @state() protected _visible = false;

    @watch("report")
    async onReportChanged() {
        this._date = undefined;
        this._ownerName = undefined;
        this._visible = false;
        if (this.report) {
            const placeTable = this._kernel.dataStore.getSource<IPlaceTable>("places");
            const placeId = this.report?.placeReportsId;
            if (placeId) {
                this._place = await placeTable.read(placeId);
                if (this._place) {
                    this._date = formatDateTime(this.report.date);
                    const owner = await this._kernel.userSettings.read(this.report.owner);
                    if (owner) {
                        this._ownerName = owner?.displayName ?? "Anonymous";
                        this._userAvatarUrl = await this._kernel.userSettings.getAvatarURL(owner);
                    }
                    this._canEdit = userCanEditPlaceReport(this._kernel.user, this.report);
                    this._visible = true;
                }
            }
        }
    }

    override render() {
        return litIf(
            this._visible,
            () =>
                html`<div class="container">
                    <sl-card class="sl-theme-light">
                        <div slot="header" class="header flex-row">
                            <div class="flex-row">
                                <sl-avatar image=${ifDefined(this._userAvatarUrl)}></sl-avatar>
                                <div class="info flex-column">
                                    <span class="name">${this._ownerName}</span>
                                    <span class="date">${this._date}</span>
                                </div>
                            </div>
                            ${litIf(
                                this._canEdit,
                                () =>
                                    html` <div class="flex-row">
                                        <sl-icon-button
                                            name="trash3"
                                            @click=${() => {
                                                if (this.report) {
                                                    this._raiseEvent("ds-delete", {
                                                        id: this.report.id,
                                                    });
                                                }
                                            }}
                                        ></sl-icon-button>
                                        <sl-icon-button
                                            name="pencil-fill"
                                            @click=${() => {
                                                if (this.report && this._place) {
                                                    this._raiseEvent("ds-edit", {
                                                        report: this.report,
                                                        place: this._place,
                                                    });
                                                }
                                            }}
                                        ></sl-icon-button>
                                    </div>`
                            )}
                        </div>
                        <div class="flex-column">
                            ${litIf(
                                this.showPlaceDetails && this._place,
                                () =>
                                    html`<div
                                            class="flex-row"
                                            style="width:100%;justify-content:space-between;"
                                        >
                                            <h3 class="trip-report-place-name">
                                                ${this._place?.data.name}
                                            </h3>
                                            <a
                                                @click=${() =>
                                                    (window.location.href = `/#/place/${this._place!.id}`)}
                                                >View Place</a
                                            >
                                        </div>
                                        <ds-place-details
                                            .place=${this._place}
                                            showSimple
                                        ></ds-place-details>`
                            )}
                            <div class="properties">
                                <span>Overall</span>
                                <sl-rating
                                    readonly
                                    value=${ifDefined(this.report?.rating ?? undefined)}
                                ></sl-rating>
                                ${litIf(
                                    !!this.report?.visibility &&
                                        this.report?.visibility !== ReportVisibility.UNSPECIFIED,
                                    () => html`
                                        <span>Visibility</span>
                                        <span>${this.report?.visibility}</span>
                                    `
                                )}
                                ${litIf(
                                    !!this.report?.swell &&
                                        this.report?.swell !== ReportSwell.UNSPECIFIED,
                                    () => html`
                                        <span>Swell</span>
                                        <span>${this.report?.swell}</span>
                                    `
                                )}
                                ${litIf(
                                    !!this.report?.wind &&
                                        this.report?.wind !== ReportWind.UNSPECIFIED,
                                    () => html`
                                        <span>Wind</span>
                                        <span>${this.report?.wind}</span>
                                    `
                                )}
                                ${litIf(
                                    !!this.report?.waterTemp,
                                    () => html`
                                        <span>Water temp</span>
                                        <ds-temperature
                                            .celsius=${this.report?.waterTemp as number}
                                        ></ds-temperature>
                                    `
                                )}
                            </div>
                            <p>${this.report?.text}</p>
                        </div>
                    </sl-card>
                </div>`
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-report": PlaceReport;
    }
}
